// @ts-strict-ignore
import React from 'react';
import { getIsOTAPlatform, hasAnyReleaseAdoptions } from '../../helpers';
import {
    AppStoreAvailableBuild,
    RolloutMonitoring,
    PhasedRelease,
    AppPlatform,
    Integration,
    IntegrationType,
    AppStoreType,
} from '../../types';
import {
    ReleaseComponentProps,
    GroupedMonitoringStatistic,
    Nullable,
    StepRunComponentProps,
} from '../../typescriptHelpers';
import {
    MonitoringSectionFilter,
    CalculatedDataPerSection,
    AdoptionChartWrapper,
    groupStatsByIntegration,
} from './MonitoringContainer';
import { MonitoringMetricCard, MonitoringMetricLoadingCard } from './MonitoringMetricCard';
import { MonitoringProgressCardsWrapper } from './MonitoringProgressCard';
import PhasedReleaseCard from './PhasedReleaseCard';
import { RolloutActionButtons } from './RolloutActionButtons';
import MonitoringAutomations from './MonitoringAutomations';
import Flex from 'antd/lib/flex';

export interface MonitoringMetricCardsGridProps extends ReleaseComponentProps {
    isLoading: boolean;
    isPhased: boolean;
    isRolloutSourcedFromBetaStep?: boolean;
    selectedBuild: AppStoreAvailableBuild;
    rolloutMonitoring: RolloutMonitoring;
    groupedMonitoringStatistics: GroupedMonitoringStatistic[];
    rolloutStartedAt: Nullable<string>;
    selectedSection: MonitoringSectionFilter;
    calculatedDataPerSection: CalculatedDataPerSection;
    phasedRelease: PhasedRelease;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    stabilityIntegrations?: Integration[];
    appStoreIntegrationId?: IntegrationType;
    isMultiApk: boolean;
    releaseVersion: string;
    backgroundColor?: string;
    disableLoadShimmer?: boolean;
    autoStagedRolloutAutomationId: Nullable<string>;
    autoHaltUnstablePhasedReleasesAutomationId: Nullable<string>;
    autoPromoteStablePhasedReleasesAutomationId: Nullable<string>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    isAutomatedPhasedRollout: boolean;
}

export const MonitoringMetricCardsGrid: React.FC<MonitoringMetricCardsGridProps> = (props) => {
    const {
        isLoading,
        selectedBuild,
        calculatedDataPerSection,
        selectedSection,
        phasedRelease,
        platform,
        appStoreType,
        stabilityIntegrations,
        appStoreIntegrationId,
        disableLoadShimmer,
        backgroundColor,
        rolloutStartedAt,
        isRolloutSourcedFromBetaStep,
    } = props;

    const stabilitySection =
        selectedSection === MonitoringSectionFilter.All || selectedSection === MonitoringSectionFilter.Stability ?
            calculatedDataPerSection?.stability
        :   null;

    const reviewsSection =
        (
            !isRolloutSourcedFromBetaStep &&
            (selectedSection === MonitoringSectionFilter.All || selectedSection === MonitoringSectionFilter.Reviews)
        ) ?
            calculatedDataPerSection?.userReviews
        :   null;

    const productAnalyticSection =
        selectedSection === MonitoringSectionFilter.All || selectedSection === MonitoringSectionFilter.Analytics ?
            calculatedDataPerSection?.productAnalytics
        :   null;

    const integrationGroups: {
        integrationType?: IntegrationType;
        stats: GroupedMonitoringStatistic[];
    }[] = [];
    for (const section of [stabilitySection, productAnalyticSection, reviewsSection]) {
        if (!section) {
            continue;
        }
        for (const group of groupStatsByIntegration(section.groupedStatistics)) {
            integrationGroups.push({
                integrationType: group.integrationType,
                stats: group.stats,
            });
        }
    }

    return (
        <Flex vertical gap={20}>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    rowGap: 10,
                    columnGap: 20,
                }}
            >
                {isLoading ?
                    <>
                        {Array(10)
                            .fill(0)
                            .map((_, index) => (
                                <MonitoringMetricLoadingCard key={`${index}`} disableLoadShimmer={disableLoadShimmer} />
                            ))}
                    </>
                :   <>
                        {!getIsOTAPlatform(platform) &&
                            hasAnyReleaseAdoptions(props.rolloutMonitoring?.releaseAdoptions) && (
                                <MonitoringProgressCardsWrapper>
                                    <AdoptionChartWrapper
                                        releaseAdoptions={props.rolloutMonitoring.releaseAdoptions}
                                        selectedBuild={selectedBuild}
                                        currentReleaseVersion={props.releaseVersion}
                                        isMultiApk={props.isMultiApk}
                                        stabilityIntegrations={stabilityIntegrations}
                                    />
                                </MonitoringProgressCardsWrapper>
                            )}
                        {!isRolloutSourcedFromBetaStep && (
                            <div style={{ gridColumn: 'span 2' }}>
                                <PhasedReleaseCard
                                    isAutomatedPhasedRollout={props.isAutomatedPhasedRollout}
                                    stepRunComponentProps={props.stepRunComponentProps}
                                    {...props}
                                    platform={platform}
                                    appStoreType={appStoreType}
                                    phasedRelease={phasedRelease}
                                    appStoreIntegrationId={appStoreIntegrationId}
                                    autoHaltUnstablePhasedReleasesAutomationId={
                                        props.autoHaltUnstablePhasedReleasesAutomationId
                                    }
                                    autoPromoteStablePhasedReleasesAutomationId={
                                        props.autoPromoteStablePhasedReleasesAutomationId
                                    }
                                    autoStagedRolloutAutomationId={props.autoStagedRolloutAutomationId}
                                    rolloutStartedAt={rolloutStartedAt}
                                />
                            </div>
                        )}
                        {!isRolloutSourcedFromBetaStep && (
                            <RolloutActionButtons
                                releaseId={props.releaseId}
                                appId={props.appId}
                                orgId={props.orgId}
                                stepRunComponentProps={props.stepRunComponentProps}
                            />
                        )}
                        {/*
                         * Bottom metric cards: ordered Stability → OBAA → App Store,
                         * and within each, grouped by integration. A full-width spacer
                         * row before each group forces a new row and adds vertical space,
                         * so groups read as headerless sections (Bugsnag, gap, Datadog,
                         * gap, Sentry, …) while keeping the cards in the same auto-fit
                         * grid (consistent sizing; single-card groups stay narrow).
                         */}
                        {integrationGroups.map((group, groupIndex) => (
                            <React.Fragment key={`integration-group-${groupIndex}-${group.integrationType ?? 'other'}`}>
                                <div style={{ gridColumn: '1 / -1', height: 6 }} aria-hidden />
                                {group.stats.map((monitoringStat) => (
                                    <MonitoringMetricCard
                                        key={monitoringStat.key}
                                        {...monitoringStat}
                                        backgroundColor={backgroundColor}
                                    />
                                ))}
                            </React.Fragment>
                        ))}
                    </>
                }
            </div>
            {props.rolloutMonitoring && !isRolloutSourcedFromBetaStep && (
                <MonitoringAutomations
                    {...props}
                    monitoringAutomations={props.rolloutMonitoring.monitoringAutomations}
                    checklistItems={props.rolloutMonitoring.checklistItems}
                    automationRuns={props.rolloutMonitoring.automationRuns}
                />
            )}
        </Flex>
    );
};
